import React from "react";
import { Helmet } from "react-helmet";
import { useAuthenticate } from "../../context/AuthContext";
import Loading from "../../components/Loading/Loading";
import DashboardCards from "../../components/Dashboard/DashboardCards/DashboardCards";
import SalesChart from "../../components/Dashboard/SalesChart/SalesChart";
import RecentSales from "../../components/Dashboard/RecentSales/RecentSales";
import AttendanceCard from "../../components/Dashboard/AttendanceCard/AttendanceCard";
import TopSellingDrugs from "../../components/Dashboard/DrugLists/TopSellingDrugs";
import OutOfStockDrugs from "../../components/Dashboard/DrugLists/OutOfStockDrugs";
import LowStockDrugs from "../../components/Dashboard/DrugLists/LowStockDrugs";
import LowStockNotificationModal from "./LowStockNotificationModal";
import ExpiringDrugsModal from "./ExpiringDrugsModal";
import { useDashboardData } from "../../hooks/useDashboardData";

const Dashboard = () => {
  const { user } = useAuthenticate();
  const {
    loadingDashboardData,
    dashboardData,
    subscriptionDetails,
    notificationModal,
    setNotificationModal,
    expiringDrugsModal,
    setExpiringDrugsModal,
    takingAttendance
  } = useDashboardData();

  if (loadingDashboardData) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>RxUltimate | Dashboard</title>
      </Helmet>
      
      <main id="content" role="main" className="main main-page pointer-event bg-gray-50 min-h-screen">
        {/* Loading Overlay */}
        {takingAttendance && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <Loading />
          </div>
        )}
        
        {/* Modals */}
        {notificationModal && (
          <LowStockNotificationModal 
            setModal={setNotificationModal} 
            drugList={dashboardData?.low_stock_drugs}
          />
        )}
        
        {expiringDrugsModal && (
          <ExpiringDrugsModal 
            setModal={setExpiringDrugsModal} 
            drugList={dashboardData?.expiring_drugs}
          />
        )}
        
        <div className="content container-fluid p-4 lg:p-6">
          {/* Header Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome, {user?.first_name}
                </h1>
                <p className="text-gray-600">
                  Monitor your business analytics and statistics
                </p>
              </div>
              <div className="hidden md:flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">System Online</span>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Cards */}
          <div className="mb-6">
            <DashboardCards dashboardData={dashboardData} />
          </div>

          {/* Main Dashboard Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            {/* Sales Chart */}
            <div className="lg:col-span-8">
              
                <div className="">
                  <SalesChart initialData={dashboardData} />
                </div>
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-4 lg:space-y-6">
              {/* Recent Sales */}
              
                  <RecentSales recentSales={dashboardData?.recent_sales} />
              
              {/* Attendance Card */}
              {subscriptionDetails?.package !== "Alpha" && (
                
                    <AttendanceCard 
                      dashboardData={dashboardData}
                      setTakingAttendance={(value) => {/* handle in custom hook */}}
                    />
              )}
            </div>
          </div>

          {/* Drug Lists Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-3 mt-6">
            {/* Top Selling Drugs */}
            
              <div className="">
                <TopSellingDrugs drugs={dashboardData?.top_selling_drugs} />
              </div>

            {/* Out of Stock Drugs */}
            
            <div className="">
              <OutOfStockDrugs drugs={dashboardData?.out_of_stock_drugs} />
            </div>

            {/* Low Stock Drugs */}  
            <div className="">
              <LowStockDrugs drugs={dashboardData?.low_stock_drugs} />
            </div>
            
          </div>
        </div>
      </main>
    </>
  );
};

export default Dashboard;